import { useState } from 'react';
import { Play, Pause, Volume2, VolumeX, BookOpen, Trophy, Medal, Target, Gift, Flame, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';

const TUTORIAL_SECTIONS = [
  {
    id: 'overview',
    icon: Trophy,
    title: 'How Gamification Works',
    description: 'Understanding the rewards system',
    content: `Your company uses a gamification system to recognize and reward learning achievements. Every time you complete a simulation, you earn CPD (Continuing Professional Development) points. These points track your progress and can be redeemed for rewards.

The system includes:
• **CPD Points** - Earned for completing simulations, reaching goals, and maintaining streaks
• **Badges** - Special achievements unlocked by meeting specific criteria  
• **Milestones** - Major accomplishments with bonus rewards
• **Rewards Store** - Redeem your points for prizes and perks`,
  },
  {
    id: 'cpd',
    icon: Medal,
    title: 'Earning CPD Points',
    description: 'How to maximize your points',
    content: `CPD points are the core currency of the gamification system. Here's how you earn them:

**Base Points:**
• Complete any simulation: +5 points (default)

**Bonus Points:**
• Reach the goal score: +10 bonus points
• Get a perfect 100% score: +15 bonus points

**Streak Bonuses:**
• 7-day streak: +25 bonus points
• 14-day streak: +50 bonus points
• 30-day streak: +100 bonus points

**Pro Tips:**
• Practice daily to build your streak
• Aim for goal scores to earn bonus points
• Check your monthly progress against the target`,
  },
  {
    id: 'badges',
    icon: Medal,
    title: 'Unlocking Badges',
    description: 'Achievement recognition',
    content: `Badges recognize specific achievements and milestones. Your company has configured custom badges that align with your learning goals.

**Common Badge Types:**
• **Completion Badges** - Complete X number of simulations
• **Performance Badges** - Achieve high scores consistently
• **Streak Badges** - Maintain daily practice habits
• **Mastery Badges** - Excel in specific areas

**How to Earn Badges:**
1. View available badges on your Progress page
2. Check the criteria for each badge
3. Work toward meeting the requirements
4. Badges are automatically awarded when criteria are met
5. Some badges come with bonus CPD points!`,
  },
  {
    id: 'milestones',
    icon: Target,
    title: 'Reaching Milestones',
    description: 'Major achievements with rewards',
    content: `Milestones are significant achievements that mark your learning journey. Unlike badges, milestones often have larger targets and more substantial rewards.

**Examples of Milestones:**
• Complete 50 simulations
• Earn 500 CPD points
• Maintain a 30-day streak
• Achieve 90%+ average score

**Milestone Rewards May Include:**
• Bonus CPD points
• Certificates
• Physical prizes
• Special recognition

Your progress toward each milestone is tracked automatically. Check your Progress page to see how close you are to your next milestone!`,
  },
  {
    id: 'streaks',
    icon: Flame,
    title: 'Building Streaks',
    description: 'Consistency rewards',
    content: `Streaks reward consistent daily practice. A streak is counted when you complete at least one simulation per day.

**How Streaks Work:**
• Complete at least one simulation to maintain your streak
• Your streak resets if you miss a day
• Streak bonuses are awarded at 7, 14, and 30 days

**Streak Tips:**
• Set a daily reminder to practice
• Even a quick simulation counts!
• Your streak is based on your timezone
• View your current streak on the Progress page

**Streak Bonuses:**
Reaching streak milestones awards bonus CPD points, encouraging regular practice and learning habits.`,
  },
  {
    id: 'rewards',
    icon: Gift,
    title: 'Redeeming Rewards',
    description: 'Using your CPD points',
    content: `Your earned CPD points can be redeemed for rewards in the company Rewards Store.

**How to Redeem:**
1. Go to your Progress page
2. View available rewards and their point costs
3. Click "Redeem" on a reward you want
4. Your redemption will be reviewed by an admin
5. You'll be notified when your reward is ready

**Redemption Notes:**
• Points are deducted upon redemption
• Some rewards have limited quantities
• Redemptions may require approval
• Check reward descriptions for details

**Types of Rewards:**
Rewards vary by company but may include gift cards, company swag, extra time off, certificates, and more!`,
  },
];

const FAQ_ITEMS = [
  {
    question: 'Why did my streak reset?',
    answer: 'Streaks reset if you don\'t complete at least one simulation in a calendar day (based on your timezone). Even completing one quick simulation will maintain your streak.',
  },
  {
    question: 'When do I receive my CPD points?',
    answer: 'Points are awarded immediately after you complete and submit a simulation. You can view your updated total on your Progress page.',
  },
  {
    question: 'Can I see which badges I\'m close to earning?',
    answer: 'Yes! Visit your Progress page to see all available badges and your progress toward unlocking them.',
  },
  {
    question: 'How long does reward redemption take?',
    answer: 'Most redemptions are reviewed within 1-2 business days. Physical rewards may take additional time for fulfillment.',
  },
  {
    question: 'Do CPD points expire?',
    answer: 'Point expiration policies vary by company. Check with your administrator for specific details.',
  },
];

export function GamificationTutorial() {
  const [expandedSection, setExpandedSection] = useState<string | null>('overview');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-xl bg-primary/10">
          <BookOpen className="h-8 w-8 text-primary" />
        </div>
        <div>
          <h2 className="text-2xl font-bold">How It Works</h2>
          <p className="text-muted-foreground">Learn how to earn points, unlock badges, and redeem rewards</p>
        </div>
      </div>

      {/* Tutorial Sections */}
      <div className="grid gap-4">
        {TUTORIAL_SECTIONS.map((section) => (
          <Card 
            key={section.id} 
            className={`transition-all cursor-pointer ${expandedSection === section.id ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <section.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{section.title}</CardTitle>
                    <CardDescription>{section.description}</CardDescription>
                  </div>
                </div>
                {expandedSection === section.id ? (
                  <ChevronUp className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
            </CardHeader>
            {expandedSection === section.id && (
              <CardContent className="pt-2">
                <div className="prose prose-sm max-w-none text-muted-foreground">
                  {section.content.split('\n').map((paragraph, i) => {
                    if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                      return <h4 key={i} className="font-semibold text-foreground mt-4 mb-2">{paragraph.replace(/\*\*/g, '')}</h4>;
                    }
                    if (paragraph.startsWith('• ')) {
                      return (
                        <p key={i} className="flex items-start gap-2 my-1">
                          <span className="text-primary">•</span>
                          <span dangerouslySetInnerHTML={{ 
                            __html: paragraph.substring(2).replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')
                          }} />
                        </p>
                      );
                    }
                    if (paragraph.match(/^\d+\./)) {
                      return (
                        <p key={i} className="flex items-start gap-2 my-1">
                          <span className="text-primary font-medium">{paragraph.match(/^\d+/)?.[0]}.</span>
                          <span>{paragraph.replace(/^\d+\.\s*/, '')}</span>
                        </p>
                      );
                    }
                    return paragraph.trim() ? (
                      <p key={i} className="my-2" dangerouslySetInnerHTML={{ 
                        __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')
                      }} />
                    ) : null;
                  })}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* FAQ Section */}
      <Card>
        <CardHeader>
          <CardTitle>Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {FAQ_ITEMS.map((faq, index) => (
              <AccordionItem key={index} value={`faq-${index}`}>
                <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* Quick Reference */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <Trophy className="h-8 w-8 mx-auto mb-2 text-primary" />
              <p className="font-semibold">Earn Points</p>
              <p className="text-sm text-muted-foreground">Complete simulations</p>
            </div>
            <div>
              <Medal className="h-8 w-8 mx-auto mb-2 text-warning" />
              <p className="font-semibold">Unlock Badges</p>
              <p className="text-sm text-muted-foreground">Achieve goals</p>
            </div>
            <div>
              <Flame className="h-8 w-8 mx-auto mb-2 text-destructive" />
              <p className="font-semibold">Build Streaks</p>
              <p className="text-sm text-muted-foreground">Practice daily</p>
            </div>
            <div>
              <Gift className="h-8 w-8 mx-auto mb-2 text-success" />
              <p className="font-semibold">Redeem Rewards</p>
              <p className="text-sm text-muted-foreground">Claim prizes</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
